import React, { FC } from 'react';
import clsx from 'clsx';
import isNumber from 'lodash/isNumber';

import { green, red, blue, yellow } from '@mui/material/colors';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { formatDate } from 'shared/functions';
import { useTranslation } from 'react-i18next';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Activity, activityLabels } from 'shared/mappings/activityLabels';
import { BaseProps } from 'shared/types/base';

const useCreditScoreClasses = makeStyles(theme => ({
  icon: {
    color: green[400],
    position: 'relative',
    marginLeft: theme.spacing(0.5),
  },
  affirmation: {
    color: blue[400],
  },
  stable: {
    color: blue[400],
  },
  better: {
    color: green[400],
  },
  worse: {
    color: red[400],
  },
  new: {
    color: yellow[400],
  },
  none: {
    display: 'none',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
  },
}));

type ScoreChangeIndicatorProps = {
  scoreOld?: number | string;
  scoreChangeDate?: string;
  activity: string;
  activityMapping: Record<string, Activity>;
  showLabel?: boolean;
} & BaseProps;

const ScoreChangeIndicator: FC<ScoreChangeIndicatorProps> = ({
  scoreOld,
  scoreChangeDate,
  activity,
  activityMapping,
  showLabel,
}) => {
  const { t } = useTranslation();
  const creditScoreClasses = useCreditScoreClasses();
  const mappedActivity = activityMapping[activity];
  const activityLabel = activityLabels[mappedActivity];

  const activityClasses: Record<Activity, string> = {
    [Activity.Downgrade]: creditScoreClasses.worse,
    [Activity.Upgrade]: creditScoreClasses.better,
    [Activity.FirstScore]: creditScoreClasses.new,
    [Activity.Affirmation]: creditScoreClasses.affirmation,
    [Activity.None]: creditScoreClasses.none,
    [Activity.Stable]: creditScoreClasses.stable,
  };

  let tooltipTitle = '';
  if (scoreOld && scoreChangeDate) {
    tooltipTitle = `Previous value: ${scoreOld}${
      isNumber(scoreOld) ? '/10' : ''
    }, change as of: ${formatDate(scoreChangeDate)}`;
  }

  return (
    <Tooltip title={tooltipTitle}>
      <div className={creditScoreClasses.wrapper}>
        {showLabel && <Typography>{t(activityLabel)}</Typography>}
        {activity && (
          <FiberManualRecordIcon
            className={clsx(creditScoreClasses.icon, activityClasses[mappedActivity])}
          />
        )}
      </div>
    </Tooltip>
  );
};

export default ScoreChangeIndicator;
